import { API_ORIGIN } from "@/lib/apiOrigin";
// Phase 44.26 — Card "Identificazione pacco" riusabile.
//
// Mostra al cliente le istruzioni per identificare il pacco prima del ritiro:
//   - Numero ordine grande (#ABCD1234)
//   - Righe di firma "Ritiro / Consegna"
//   - Bottoni: Copia numero, Scarica etichetta PDF, Stampa
//
// Usato in:
//   - customer/OrderDetail.jsx (banner persistente fino al ritiro)
//   - customer/NewOrder.jsx step 6 (subito dopo creazione ordine, sia per
//     ordini coperti da pack/sub che per Stripe completati)
//
// Mantiene UN UNICO punto di verità per testo + bottoni → niente duplicazione.

import { Package, Copy, FileText, Navigation } from "lucide-react";
import { toast } from "sonner";
import { tokenStore } from "@/lib/api";

const BASE = API_ORIGIN;

export default function ParcelLabelCard({ orderId, compact = false, testid = "parcel-label-card" }) {
  if (!orderId) return null;
  const code = `#${orderId.slice(0, 8).toUpperCase()}`;

  const downloadLabel = async (printAfter = false) => {
    try {
      const token = tokenStore.get();
      const res = await fetch(`${BASE}/api/orders/${orderId}/label.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Etichetta non disponibile");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      if (printAfter) {
        const w = window.open(objectUrl, "_blank");
        if (!w) toast.error("Disabilita il blocco popup per stampare");
        else setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 800);
      } else {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `dapnow-label-${orderId.slice(0, 8).toUpperCase()}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
      }
    } catch (e) {
      toast.error(e.message || "Errore etichetta");
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success("Numero ordine copiato"),
      () => toast.error("Copia non riuscita"),
    );
  };

  return (
    <section
      className="card-soft p-5 sm:p-6 border-l-4 border-l-dapnow-blue bg-dapnow-blue-soft/40"
      data-testid={testid}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dapnow-blue text-white shrink-0">
          <Package className="h-6 w-6" />
        </span>
        <div className="flex-1 min-w-[260px]">
          <p className="overline">Identificazione pacco</p>
          <h2 className="font-display text-xl font-bold tracking-tight mt-0.5">
            Prima del ritiro, scrivi sul pacco
          </h2>

          <div
            className="mt-3 rounded-2xl border border-dashed border-dapnow-blue/40 bg-white p-4 font-mono text-sm leading-relaxed text-dapnow-ink"
            data-testid={`${testid}-instructions`}
          >
            <div className="font-extrabold text-2xl tracking-tight" data-testid={`${testid}-code`}>
              {code}
            </div>
            <div className="mt-2">Ritiro: <span className="text-dapnow-muted">__________________</span></div>
            <div className="mt-1">Consegna: <span className="text-dapnow-muted">__________________</span></div>
          </div>

          {!compact && (
            <p className="mt-3 text-sm text-dapnow-ink">
              Il driver <strong>firmerà accanto a "Ritiro"</strong> e scatterà una foto al momento del ritiro.
              Alla consegna, il destinatario (o il driver) <strong>firmerà accanto a "Consegna"</strong> e verrà scattata una seconda foto come prova.
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyCode}
              data-testid={`${testid}-copy-btn`}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-dapnow-line bg-white text-sm font-semibold hover:bg-dapnow-bg transition"
            >
              <Copy className="h-4 w-4" /> Copia numero
            </button>
            <button
              type="button"
              onClick={() => downloadLabel(false)}
              data-testid={`${testid}-download-btn`}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue-hover transition"
            >
              <FileText className="h-4 w-4" /> Scarica etichetta PDF
            </button>
            <button
              type="button"
              onClick={() => downloadLabel(true)}
              data-testid={`${testid}-print-btn`}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl border border-dapnow-line bg-white text-sm font-semibold hover:bg-dapnow-bg transition"
            >
              <Navigation className="h-4 w-4 rotate-45" /> Stampa
            </button>
          </div>
          <p className="mt-2 text-xs text-dapnow-muted">
            L'etichetta PDF è opzionale. Il metodo principale resta scrivere a mano numero ordine e righe firma sul pacco.
          </p>
        </div>
      </div>
    </section>
  );
}
